import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type {
  GpuBudget,
  GpuBudgetLineCreateRequest,
  GpuBudgetLineUpdateRequest,
  GpuBudgetNameRequest,
} from '../types/models/GpuBudget';
import type {
  ApiResponseGpuBudget,
  ApiResponseGpuBudgetDelete,
  ApiResponseGpuBudgetLineCreate,
} from '../types/api/ApiResponseGpuBudget';
import type { GpuJsendEnvelope } from './gpuServerResponse';
import { readJsendBody } from './gpuServerResponse';
import type { GpuBudgetWriteError } from './gpuBudgetResponse';
import { budgetWriteError, budgetWriteThrown } from './gpuBudgetResponse';
import { isGpuBudget } from './useGpuBudget';

/** A write that returns the whole re-priced budget. */
export type GpuBudgetWriteOutcome = { status: 'success'; budget: GpuBudget } | GpuBudgetWriteError;

/** Add a line: the re-priced budget plus the new line's id. */
export type GpuBudgetLineAddOutcome = { status: 'success'; budget: GpuBudget; lineId: number } | GpuBudgetWriteError;

/** Delete a budget: its id. */
export type GpuBudgetDeleteOutcome = { status: 'success'; id: number } | GpuBudgetWriteError;

export interface UseGpuBudgetMutationsReturn {
  /** Every function never throws, and never retries: a failed write is reported, not resubmitted. */
  createBudget: (name: string) => Promise<GpuBudgetWriteOutcome>;
  renameBudget: (id: number, name: string) => Promise<GpuBudgetWriteOutcome>;
  deleteBudget: (id: number) => Promise<GpuBudgetDeleteOutcome>;
  addLine: (id: number, body: GpuBudgetLineCreateRequest) => Promise<GpuBudgetLineAddOutcome>;
  /** `body` must hold at least one field (never an empty PATCH). */
  updateLine: (id: number, lineId: number, body: GpuBudgetLineUpdateRequest) => Promise<GpuBudgetWriteOutcome>;
  deleteLine: (id: number, lineId: number) => Promise<GpuBudgetWriteOutcome>;
  /** A write is in flight. */
  pending: boolean;
}

const BASE = '/datacenters/gpu-servers/budgets';

const pickBudget = (body: GpuJsendEnvelope): { budget: GpuBudget } | null => {
  const budget = (body as ApiResponseGpuBudget).data?.budget;
  return isGpuBudget(budget) ? { budget } : null;
};

const pickLineAdd = (body: GpuJsendEnvelope): { budget: GpuBudget; lineId: number } | null => {
  const data = (body as ApiResponseGpuBudgetLineCreate).data;
  return isGpuBudget(data?.budget) && typeof data?.line_id === 'number'
    ? { budget: data.budget, lineId: data.line_id }
    : null;
};

const pickDelete = (body: GpuJsendEnvelope): { id: number } | null => {
  const data = (body as ApiResponseGpuBudgetDelete).data;
  return data?.deleted === true && typeof data.id === 'number' ? { id: data.id } : null;
};

/**
 * The GPU budget writes (docs/api-types/gpu-budgets.md): create / rename / delete a budget, add /
 * change / remove a line. Each returns a discriminated outcome — `success` with what the backend
 * returned, or `error` with the verbatim message, the `data.<field>` map and a `reason` that tells the
 * caller where to show it and what to refetch (see `GpuBudgetErrorReason`). Money never moves: these
 * are allowed under impersonation.
 */
export const useGpuBudgetMutations = (): UseGpuBudgetMutationsReturn => {
  const apiFetch = useApiFetch();
  const [pendingCount, setPendingCount] = useState(0);

  const send = useCallback(
    async <T>(
      path: string,
      method: string,
      payload: object | null,
      pick: (body: GpuJsendEnvelope) => T | null,
      fallback: string,
    ): Promise<({ status: 'success' } & T) | GpuBudgetWriteError> => {
      setPendingCount((n) => n + 1);
      try {
        const response = await apiFetch(path, {
          method,
          ...(payload !== null ? { body: JSON.stringify(payload) } : {}),
        });
        const body = await readJsendBody<GpuJsendEnvelope>(response);
        const picked = body?.status === 'success' ? pick(body) : null;
        if (picked !== null) {
          return { status: 'success', ...picked };
        }
        return budgetWriteError(body, fallback);
      } catch {
        return budgetWriteThrown();
      } finally {
        setPendingCount((n) => n - 1);
      }
    },
    [apiFetch],
  );

  const createBudget = useCallback(
    (name: string) =>
      send(BASE, 'POST', { name } satisfies GpuBudgetNameRequest, pickBudget, 'Could not create the budget.'),
    [send],
  );

  const renameBudget = useCallback(
    (id: number, name: string) =>
      send(
        `${BASE}/${id}`,
        'PATCH',
        { name } satisfies GpuBudgetNameRequest,
        pickBudget,
        'Could not rename the budget.',
      ),
    [send],
  );

  const deleteBudget = useCallback(
    (id: number) => send(`${BASE}/${id}`, 'DELETE', null, pickDelete, 'Could not delete the budget.'),
    [send],
  );

  const addLine = useCallback(
    (id: number, body: GpuBudgetLineCreateRequest) =>
      send(`${BASE}/${id}/lines`, 'POST', body, pickLineAdd, 'Could not add the line.'),
    [send],
  );

  const updateLine = useCallback(
    (id: number, lineId: number, body: GpuBudgetLineUpdateRequest) =>
      send(`${BASE}/${id}/lines/${lineId}`, 'PATCH', body, pickBudget, 'Could not change the line.'),
    [send],
  );

  const deleteLine = useCallback(
    (id: number, lineId: number) =>
      send(`${BASE}/${id}/lines/${lineId}`, 'DELETE', null, pickBudget, 'Could not remove the line.'),
    [send],
  );

  return { createBudget, renameBudget, deleteBudget, addLine, updateLine, deleteLine, pending: pendingCount > 0 };
};

export default useGpuBudgetMutations;
